import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subject, takeUntil, filter } from 'rxjs';
import { NotificationService } from '../notification.service';
import { select, Store } from '@ngrx/store';
import { selectCurrentUser } from '@app/store/user/user.selectors';

interface NotificationData {
    type: string;
    webIcon?: string;
    webColor?: string;
    webRoute?: string;
    priority?: 'low' | 'medium' | 'high';
    action?: string;
    [key: string]: any;
}

interface Notification {
    id: number;
    title: string;
    body: string;
    data: NotificationData;
    readAt: Date | null;
    createdOn: Date;
    imageUrl?: string;
}

@Component({
    selector: 'app-notifications-panel',
    standalone: false,
    templateUrl: './notifications-panel.component.html',
    styleUrl: './notifications-panel.component.scss',
})
export class NotificationsPanelComponent implements OnInit, OnDestroy {
    notifications: Notification[] = [];
    isLoading = false;
    currentUser: any = null;
    companyColor = '#003CA6';
    private destroy$ = new Subject<void>();

    @ViewChild('notificationPanel') notificationPanel!: OverlayPanel;

    constructor(
        private notificationService: NotificationService,
        private router: Router,
        private store: Store
    ) {}

    ngOnInit() {
        this.store
            .pipe(
                select(selectCurrentUser),
                filter((user) => !!user),
                takeUntil(this.destroy$)
            )
            .subscribe((user: any) => {
                this.currentUser = user;
                this.companyColor = user.company?.color || '#003CA6';
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggle(event: Event) {
        if (!this.notificationPanel.overlayVisible) {
            this.loadNotifications();
        }
        this.notificationPanel.toggle(event);
    }

    loadNotifications() {
        this.isLoading = true;
        this.notificationService
            .getUnreadNotifications(10)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (notifications: any[]) => {
                    this.notifications = notifications;
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Error al cargar notificaciones:', error);
                    this.notifications = [];
                    this.isLoading = false;
                }
            });
    }

    onNotificationClick(notification: Notification) {
       this.markAsRead(notification);

        const data = notification.data;
        const route = data?.webRoute || '/home';
        
        console.log('Notification clicked:', {
            type: data?.type,
            route: route,
            action: data?.action,
            priority: data?.priority,
            fullData: data
        });

        this.notificationPanel.hide();

        const action = data?.action;
        
        if (action) {
            this.handleSpecialAction(action, data, route);
        } else {
            this.router.navigate([route]);
        }
    }

    private handleSpecialAction(action: string, data: NotificationData, defaultRoute: string) {
        switch(action) {
            case 'download_file':
                if (data['downloadUrl']) {
                    window.open(data['downloadUrl'], '_blank');
                }
                break;
                
            case 'open_external':
                if (data['externalUrl']) {
                    window.open(data['externalUrl'], '_blank');
                } else {
                    this.router.navigate([defaultRoute]);
                }
                break;
                
            case 'open_vacations':
                                    this.router.navigate([defaultRoute]);

                break;
            case 'open_tasks':
                
            case 'open_detail':
            default:
                const queryParams: any = {};
/*                 if (data['taskId']) queryParams.taskId = data['taskId'];
 *//*                 if (data['requisitionId']) queryParams.requisitionId = data['requisitionId'];
 */                
                this.router.navigate([defaultRoute], {
                    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
                });
        }
    }

    markAsRead(notification: Notification) {
        if (!notification.readAt) {
            this.notificationService.markAsRead(notification.id).subscribe({
                next: () => {
                    notification.readAt = new Date();
                    this.notificationService.refreshUnreadCount();
                },
                error: (error) => console.error('Error al marcar como leída:', error)
            });
        }
    }

    markAllAsRead() {
        if (this.notifications.length === 0) return;
        
        this.notificationService.markAllAsRead().subscribe({
            next: () => {
                this.notifications.forEach((n) => (n.readAt = new Date()));
                this.notificationService.refreshUnreadCount();
            },
            error: (error) =>
                console.error('Error al marcar todas como leídas:', error)
        });
    }

    viewAllNotifications() {
        this.notificationPanel.hide();
        this.router.navigate(['/home/notifications']);
    }

    getTimeAgo(date: Date | string): string {
        if (!date) return '';
        const now = new Date();
        const notificationDate = new Date(date);
        const diffMs = now.getTime() - notificationDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Ahora mismo';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHours < 24) return `Hace ${diffHours} h`;
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `Hace ${diffDays} días`;
        
        return notificationDate.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short'
        });
    }

    getNotificationIcon(notification: Notification): string {
        return notification.data?.webIcon || 'pi pi-bell';
    }

    getNotificationColor(notification: Notification): string {
        return notification.data?.webColor || '#6b7280';
    }

    getPriorityClass(notification: Notification): string {
        const priority = notification.data?.priority;
        return priority ? `priority-${priority}` : '';
    }

    hasImage(notification: Notification): boolean {
        return !!notification.imageUrl;
    }
}